import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import SeoMetaGuard from "./SeoMetaGuard";
import {
  readPersistedPublicSiteIdentity,
  resolvePublicSiteBootstrapCandidate,
} from "../features/vacationPackagesPublic/publicSiteIdentity";
import { parseLocaleFromPathname } from "../utils/localeUrl";

function logSeoBridgePerf(_message, _data = {}) {
  /* optional dev trace — ingest removed */
}

const SENSITIVE_PATH_PREFIXES = [
  "/p",
  "/admin",
  "/dashboard",
  "/search",
  "/checkout",
  "/payment",
  "/api",
];

const PUBLIC_SITE_LOOKUP_PREFIXES = ["/vacation-packages", "/vacation_packages"];

const isPathPrefixedBy = (pathname, prefix) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

const pathnameWithoutLocalePrefix = (pathname) =>
  parseLocaleFromPathname(pathname).pathnameWithoutLocale;

export const isSensitiveSeoPath = (pathname) => {
  const inner = pathnameWithoutLocalePrefix(pathname);
  return SENSITIVE_PATH_PREFIXES.some((prefix) => isPathPrefixedBy(inner, prefix));
};

export const isPublicSiteLookupPath = (pathname) => {
  const inner = pathnameWithoutLocalePrefix(pathname);
  return PUBLIC_SITE_LOOKUP_PREFIXES.some((prefix) => isPathPrefixedBy(inner, prefix));
};

const parsePositiveInt = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) {
    return null;
  }
  return Math.trunc(num);
};

export const extractPublicSiteIdentityCandidate = (search) =>
  resolvePublicSiteBootstrapCandidate({ search }).siteRef;

export default function SeoMetaGuardRuntimeBridge() {
  const location = useLocation();
  const [agentId, setAgentId] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const [siteTitle, setSiteTitle] = useState(null);
  const [siteDescription, setSiteDescription] = useState(null);
  const [siteKeywords, setSiteKeywords] = useState(null);
  const subscribedSiteRef = useMemo(() => {
    const candidate = resolvePublicSiteBootstrapCandidate({
      search: location.search,
      pathname: location.pathname,
      hostname: typeof window !== "undefined" ? window.location.hostname : "",
    });
    return candidate.siteRef || "";
  }, [location.pathname, location.search]);

  const shouldResolveByPublicSiteApi = useMemo(() => {
    if (isSensitiveSeoPath(location.pathname)) {
      return false;
    }
    return isPublicSiteLookupPath(location.pathname);
  }, [location.pathname]);

  const isPublicAgentSite = shouldResolveByPublicSiteApi && Boolean(subscribedSiteRef);

  useEffect(() => {
    let mounted = true;
    setAgentId((prev) => (prev === null ? prev : null));
    setSiteSettings((prev) => (prev == null ? prev : null));
    setSiteTitle((prev) => (prev == null ? prev : null));
    setSiteDescription((prev) => (prev == null ? prev : null));
    setSiteKeywords((prev) => (prev == null ? prev : null));

    if (!shouldResolveByPublicSiteApi) {
      return () => {
        mounted = false;
      };
    }

    if (!subscribedSiteRef) {
      return () => {
        mounted = false;
      };
    }
    logSeoBridgePerf("seo bridge uses context event only", {
      siteRef: subscribedSiteRef,
      pathname: location.pathname,
      search: location.search,
    });

    const trimText = (value) =>
      typeof value === "string" && value.trim() ? value.trim() : null;

    const syncFromPublicSiteContext = (event) => {
      if (!mounted) return;
      const nextAgentId =
        event?.detail?.agentId ?? (typeof window !== "undefined" ? window.__HR_PUBLIC_SITE_AGENT_ID : null);
      const nextSiteSettings =
        event?.detail?.siteSettings ??
        (typeof window !== "undefined" ? window.__HR_PUBLIC_SITE_SITE_SETTINGS : null);
      const nextSiteTitle =
        event?.detail?.siteTitle ??
        (typeof window !== "undefined" ? window.__HR_PUBLIC_SITE_TITLE : null);
      const nextSiteDescription =
        event?.detail?.siteDescription ??
        (typeof window !== "undefined" ? window.__HR_PUBLIC_SITE_DESCRIPTION : null);
      const nextSiteKeywords =
        event?.detail?.siteKeywords ??
        (typeof window !== "undefined" ? window.__HR_PUBLIC_SITE_KEYWORDS : null);
      logSeoBridgePerf("seo bridge identity event", {
        hasEvent: Boolean(event),
        agentId: nextAgentId ?? null,
      });
      setAgentId(parsePositiveInt(nextAgentId));
      setSiteSettings(nextSiteSettings && typeof nextSiteSettings === "object" ? nextSiteSettings : null);
      setSiteTitle(trimText(nextSiteTitle));
      setSiteDescription(trimText(nextSiteDescription));
      setSiteKeywords(trimText(nextSiteKeywords));
    };

    syncFromPublicSiteContext();

    const persisted = readPersistedPublicSiteIdentity();
    if (
      mounted &&
      persisted?.publicSiteToken === subscribedSiteRef &&
      persisted?.metadata?.agencyName
    ) {
      setSiteTitle((prev) => prev || trimText(persisted.metadata.agencyName));
    }

    window.addEventListener("hr-public-site-identity", syncFromPublicSiteContext);

    return () => {
      mounted = false;
      window.removeEventListener("hr-public-site-identity", syncFromPublicSiteContext);
    };
  }, [location.pathname, location.search, shouldResolveByPublicSiteApi, subscribedSiteRef]);

  const isPublicAgentSitePending =
    isPublicAgentSite && !agentId && !siteTitle && !siteSettings;

  return (
    <SeoMetaGuard
      agentId={agentId}
      siteSettings={siteSettings}
      publicSiteTitle={siteTitle}
      publicSiteDescription={siteDescription}
      publicSiteKeywords={siteKeywords}
      isPublicAgentSite={isPublicAgentSite}
      isPublicAgentSitePending={isPublicAgentSitePending}
    />
  );
}
